import React from 'react';
import { SimulationConfig } from './types';
import { ControlGroup } from './ControlGroup';
import { Toggle } from './Toggle';

interface HyperSpeedSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const HyperSpeedSettingsSection: React.FC<HyperSpeedSettingsSectionProps> = ({ config, setConfig }) => {
  if (config.mode !== 'ESCAPE') return null;

  return (
    <ControlGroup label="Hyper Speed / Optimization">
      <div className="flex flex-col gap-4">
        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium mb-2">
                <i className="fa-solid fa-bolt mr-1"></i>
                EXPERIMENTAL: Use this to destroy 100k+ rings without lag. Works best with "Aligned" gaps and "Stopped" rotation.
            </p>
        </div>

        <div className="flex items-center justify-between">
            <div className="flex flex-col">
                <span className="text-xs font-bold">Enable Hyper Speed Mode</span>
                <span className="text-[10px] opacity-60">Batch processes ring destruction for extreme performance.</span>
            </div>
            <Toggle
                active={config.enableHyperSpeed}
                onClick={() => setConfig(prev => ({ ...prev, enableHyperSpeed: !prev.enableHyperSpeed }))}
                color="bg-amber-500"
            />
        </div>

        {config.enableHyperSpeed && (
            <>
                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        <span className="text-xs font-bold">Simplified Visuals</span>
                        <span className="text-[10px] opacity-60">Reduces blast effects to simple lines during high speed.</span>
                    </div>
                    <Toggle
                        active={config.hyperSpeedSimplifiedVisuals}
                        onClick={() => setConfig(prev => ({ ...prev, hyperSpeedSimplifiedVisuals: !prev.hyperSpeedSimplifiedVisuals }))}
                        color="bg-amber-500"
                    />
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        <span className="text-xs font-bold">Auto-Tune Settings</span>
                        <span className="text-[10px] opacity-60">Automatically adjusts batch size based on speed.</span>
                    </div>
                    <Toggle
                        active={config.hyperSpeedAutoTune}
                        onClick={() => setConfig(prev => ({ ...prev, hyperSpeedAutoTune: !prev.hyperSpeedAutoTune }))}
                        color="bg-amber-500"
                    />
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        <span className="text-xs font-bold">Stop Shrinking</span>
                        <span className="text-[10px] opacity-60">Stops ring shrinking once destruction begins.</span>
                    </div>
                    <Toggle
                        active={config.hyperSpeedStopShrink}
                        onClick={() => setConfig(prev => ({ ...prev, hyperSpeedStopShrink: !prev.hyperSpeedStopShrink }))}
                        color="bg-amber-500"
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                        <label className="text-xs font-medium opacity-70">Trigger Threshold (0.0 - 1.0)</label>
                        <span className="text-xs font-bold">{config.hyperSpeedTriggerThreshold}</span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={config.hyperSpeedTriggerThreshold}
                        onChange={(e) => setConfig(prev => ({ ...prev, hyperSpeedTriggerThreshold: parseFloat(e.target.value) }))}
                        className="w-full accent-amber-500"
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                        <label className="text-xs font-medium opacity-70">Acceleration</label>
                        <span className="text-xs font-bold">{config.hyperSpeedAcceleration}</span>
                    </div>
                    <input
                        type="range"
                        min="0.01"
                        max="1"
                        step="0.01"
                        value={config.hyperSpeedAcceleration}
                        onChange={(e) => setConfig(prev => ({ ...prev, hyperSpeedAcceleration: parseFloat(e.target.value) }))}
                        className="w-full accent-amber-500"
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                        <label className="text-xs font-medium opacity-70">Target Time (Seconds)</label>
                        <span className="text-xs font-bold">{config.hyperSpeedTargetTime}s</span>
                    </div>
                    <input
                        type="range"
                        min="1"
                        max="60"
                        step="1"
                        value={config.hyperSpeedTargetTime}
                        onChange={(e) => setConfig(prev => ({ ...prev, hyperSpeedTargetTime: parseFloat(e.target.value) }))}
                        className="w-full accent-amber-500"
                    />
                </div>
                
                <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                        <label className="text-xs font-medium opacity-70">Batch Size (Max Rings/Frame)</label>
                        <span className="text-xs font-bold">{config.hyperSpeedBatchSize}</span>
                    </div>
                    <input
                        type="range"
                        min="10"
                        max="1000"
                        step="10"
                        value={config.hyperSpeedBatchSize}
                        onChange={(e) => setConfig(prev => ({ ...prev, hyperSpeedBatchSize: parseInt(e.target.value) }))}
                        className="w-full accent-amber-500"
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                        <label className="text-xs font-medium opacity-70">Render Limit (Visible Rings)</label>
                        <span className="text-xs font-bold">{config.hyperSpeedRenderLimit}</span>
                    </div>
                    <input
                        type="range"
                        min="10"
                        max="500"
                        step="10"
                        value={config.hyperSpeedRenderLimit}
                        onChange={(e) => setConfig(prev => ({ ...prev, hyperSpeedRenderLimit: parseInt(e.target.value) }))}
                        className="w-full accent-amber-500"
                    />
                </div>
            </>
        )}
      </div>
    </ControlGroup>
  );
};
